/**
 * W3C Trace Context propagation for the API fetch wrapper.
 *
 * The API contract (PRD_MVP_API_Contract §1.4 / §7.1) states the fetch wrapper
 * should attach `traceparent` when a trace context exists, so the frontend span
 * joins the backend trace instead of starting a disconnected one.
 *
 * Format: `00-{32 hex trace-id}-{16 hex span-id}-{2 hex flags}`
 */

const TRACEPARENT_HEADER = 'traceparent';
const VERSION = '00';
const SAMPLED_FLAGS = '01';

let sessionTraceId: string | null = null;

function randomHex(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Return the trace id for the current page session, creating it on first use.
 *
 * One trace id is reused for the page session so every request from the same
 * page load correlates to a single trace; each request gets a fresh span id.
 */
export function getSessionTraceId(): string {
  if (!sessionTraceId) {
    sessionTraceId = randomHex(16);
  }
  return sessionTraceId;
}

/**
 * Build a W3C `traceparent` value for a new span within the session trace.
 */
export function createTraceparent(): string {
  return `${VERSION}-${getSessionTraceId()}-${randomHex(8)}-${SAMPLED_FLAGS}`;
}

/**
 * Attach `traceparent` to the given headers unless the caller already set one.
 */
export function applyTraceparent(headers: Headers): void {
  if (!headers.has(TRACEPARENT_HEADER)) {
    headers.set(TRACEPARENT_HEADER, createTraceparent());
  }
}

/**
 * Reset the session trace id. Test-only helper.
 */
export function resetTraceContext(): void {
  sessionTraceId = null;
}
